import type { LucideIcon } from 'lucide-react';
import { TONE_CLASSES, TONE_SOLID, statusTone, type StatusTone } from './statusTone';

export type StatusBadgeVariant = 'success' | 'warning' | 'error' | 'info' | 'neutral' | 'purple';
export type StatusBadgeSize = 'sm' | 'md';

export interface StatusBadgeProps {
  /**
   * Semantic tone — the preferred API. Says what the status MEANS, so the same
   * meaning is the same colour in every module. See `statusTone.ts`.
   */
  tone?: StatusTone;
  /**
   * Resolve the tone from the module's own status word.
   * `<StatusBadge status={ticket.status}>{label}</StatusBadge>`
   */
  status?: string | null;
  /**
   * Raw colour choice. Predates `tone` and is kept for the ~40 call sites that
   * still pass it — prefer `tone`/`status`, which carry meaning rather than a
   * colour opinion.
   */
  variant?: StatusBadgeVariant;
  size?: StatusBadgeSize;
  icon?: LucideIcon;
  pulse?: boolean;
  className?: string;
  /**
   * Hover text. Required whenever the badge shows an abbreviation — the
   * shortened form is the visible label, and this is where the full one lives.
   */
  title?: string;
  /** Accessible name. Pair with `title` on any abbreviated badge. */
  'aria-label'?: string;
  /** Escape hatch for tests and query hooks. */
  'data-testid'?: string;
  children: React.ReactNode;
}

/**
 * Legacy variants mapped onto tones so both APIs render one set of colours.
 * `purple` has no tone of its own — nothing in the five means "purple" — so it
 * lands on `active`, which is what its call sites use it for.
 */
const VARIANT_TO_TONE: Record<StatusBadgeVariant, StatusTone> = {
  success: 'done',
  warning: 'attention',
  error: 'blocked',
  info: 'active',
  neutral: 'neutral',
  purple: 'active',
};

const SIZE_CLASSES: Record<StatusBadgeSize, string> = {
  sm: 'text-xs px-2 py-0.5',
  md: 'text-sm px-2.5 py-1',
};

export function StatusBadge({
  tone,
  status,
  variant,
  size = 'sm',
  icon: Icon,
  pulse = false,
  className = '',
  title,
  'aria-label': ariaLabel,
  'data-testid': testId,
  children,
}: StatusBadgeProps) {
  // tone wins, then an explicit status word, then the legacy variant.
  const resolved: StatusTone =
    tone ??
    (status !== undefined ? statusTone(status) : undefined) ??
    (variant ? VARIANT_TO_TONE[variant] : 'neutral');

  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full font-bold ${TONE_CLASSES[resolved]} ${SIZE_CLASSES[size]} ${className}`.trim()}
      title={title}
      aria-label={ariaLabel}
      data-testid={testId}
    >
      {pulse && <span className={`h-1.5 w-1.5 animate-pulse rounded-full ${TONE_SOLID[resolved]}`} />}
      {Icon && <Icon className={`${size === 'sm' ? 'h-3 w-3' : 'h-4 w-4'} shrink-0`} />}
      {children}
    </span>
  );
}
